/**
 * Phase 5 Task 8 — DialerControls.
 *
 * Agent-facing preview dialer controls: shows the queued contact and
 * exposes Next Contact / Dial / Skip actions.
 */
import type { JSX } from 'react'
import { Button } from 'primereact/button'

interface Contact {
  id: string
  name: string
  phone: string
}

interface Props {
  contact: Contact | null
  campaignName: string
  onNextContact(): void
  onDial(): void
  onSkip(): void
}

export function DialerControls({
  contact,
  campaignName,
  onNextContact,
  onDial,
  onSkip,
}: Props): JSX.Element {
  return (
    <div className="flex flex-col gap-3 p-4 border rounded bg-white shadow-sm" data-testid="dialer-controls">
      <div className="text-xs text-gray-500 uppercase tracking-wide">
        Campaign: <span className="font-semibold text-gray-700">{campaignName}</span>
      </div>

      {contact ? (
        <div className="flex flex-col gap-1" data-testid="contact-info">
          <div className="font-semibold text-base" data-testid="contact-name">
            {contact.name}
          </div>
          <div className="text-sm text-gray-600" data-testid="contact-phone">
            {contact.phone}
          </div>
        </div>
      ) : (
        <div className="text-sm text-gray-400 italic" data-testid="no-contact">
          No contact loaded — press &quot;Next Contact&quot; to load one.
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <Button
          label="Next Contact"
          icon="pi pi-forward"
          severity="secondary"
          size="small"
          onClick={onNextContact}
          aria-label="Next Contact"
        />
        <Button
          label="Dial"
          icon="pi pi-phone"
          severity="success"
          size="small"
          disabled={!contact}
          onClick={onDial}
          aria-label="Dial"
        />
        <Button
          label="Skip"
          icon="pi pi-step-forward"
          severity="warning"
          size="small"
          disabled={!contact}
          onClick={onSkip}
          aria-label="Skip"
        />
      </div>
    </div>
  )
}
